// ============================================================================
// todo-client-mod -- TodoPanel
//
// 待办事项管理面板容器（Slot 内容组件）。
// 集成：分组选择器 + Todo 列表 + 筛选工具栏 + 编辑器弹窗 + 分组管理弹窗。
// 响应式布局：移动端自动堆叠，桌面端左右分栏。
// ============================================================================
import * as React from 'react';
import { useTheme } from '../styles/use-theme.js';
import { useResponsive } from '../styles/use-responsive.js';
import { useTodos } from '../hooks/use-todos.js';
import { useDeleteTodo } from '../hooks/use-delete-todo.js';
import { TodoListItem } from './todo-list-item.js';
import { TodoEditor } from './todo-editor.js';
import { TodoGroupSelector } from './todo-group-selector.js';
import { TodoGroupManager } from './todo-group-manager.js';
import { PlusIcon, EmptyIcon } from './icons.js';
import { radius, typography, transition } from '../styles/theme.js';
const STATUS_OPTIONS = [
    { id: 'all', label: '全部' },
    { id: 'pending', label: '待处理' },
    { id: 'in_progress', label: '进行中' },
    { id: 'completed', label: '已完成' },
    { id: 'cancelled', label: '已取消' },
];
const PRIORITY_OPTIONS = [
    { id: 'all', label: '全部' },
    { id: 'urgent', label: '紧急' },
    { id: 'high', label: '高' },
    { id: 'medium', label: '中' },
    { id: 'low', label: '低' },
];
const SORT_OPTIONS = [
    { id: 'dueDate', label: '截止日期' },
    { id: 'createdAt', label: '创建时间' },
    { id: 'priority', label: '优先级' },
];
const PRIORITY_WEIGHT = { urgent: 4, high: 3, medium: 2, low: 1 };
export function TodoPanel() {
    const { theme } = useTheme();
    const { isMobile } = useResponsive();
    const [selectedGroupId, setSelectedGroupId] = React.useState(undefined);
    const [filterStatus, setFilterStatus] = React.useState('all');
    const [filterPriority, setFilterPriority] = React.useState('all');
    const [sortBy, setSortBy] = React.useState('dueDate');
    const [sortOrder, setSortOrder] = React.useState('asc');
    const [editorOpen, setEditorOpen] = React.useState(false);
    const [editingTodo, setEditingTodo] = React.useState(null);
    const [groupManagerOpen, setGroupManagerOpen] = React.useState(false);
    const [deleteConfirm, setDeleteConfirm] = React.useState(null);
    const { todos, pagination, loading, error, refresh, setPage, setGroupId } = useTodos({ pageSize: 10 });
    const { deleting, deleteTodo } = useDeleteTodo();
    const [statusFilter, setStatusFilter] = React.useState(undefined);
    React.useEffect(() => { setGroupId(selectedGroupId); }, [selectedGroupId, setGroupId]);
    React.useEffect(() => { setStatusFilter(filterStatus === 'all' ? undefined : filterStatus); }, [filterStatus]);
    const handleCreate = () => { setEditingTodo(null); setEditorOpen(true); };
    const handleEdit = (todo) => { setEditingTodo(todo); setEditorOpen(true); };
    const handleEditorClose = () => { setEditorOpen(false); setEditingTodo(null); };
    const handleEditorSuccess = () => { setEditorOpen(false); setEditingTodo(null); refresh(); };
    const handleComplete = async (_todo) => { refresh(); };
    const handleDeleteConfirm = async () => {
        if (!deleteConfirm)
            return;
        const success = await deleteTodo(deleteConfirm._id);
        if (success) {
            setDeleteConfirm(null);
            refresh();
        }
    };
    const filteredTodos = React.useMemo(() => {
        let result = todos;
        if (statusFilter)
            result = result.filter(t => t.status === statusFilter);
        if (filterPriority !== 'all')
            result = result.filter(t => t.priority === filterPriority);
        return result;
    }, [todos, statusFilter, filterPriority]);
    const sortedTodos = React.useMemo(() => {
        const sorted = [...filteredTodos];
        sorted.sort((a, b) => {
            let cmp = 0;
            switch (sortBy) {
                case 'dueDate':
                    cmp = (a.dueDate ?? 0) - (b.dueDate ?? 0);
                    break;
                case 'createdAt':
                    cmp = a._createdAt - b._createdAt;
                    break;
                case 'priority':
                    cmp = PRIORITY_WEIGHT[a.priority] - PRIORITY_WEIGHT[b.priority];
                    break;
            }
            return sortOrder === 'asc' ? cmp : -cmp;
        });
        return sorted;
    }, [filteredTodos, sortBy, sortOrder]);
    /* -- 通用元素 -- */
    const plusIconNode = React.createElement(PlusIcon);
    const emptyIconNode = React.createElement(EmptyIcon);
    const toolSelect = {
        padding: '6px 10px',
        border: `1px solid ${theme.border.default}`,
        borderRadius: radius.md,
        fontSize: typography.fontSize.sm,
        outline: 'none',
        backgroundColor: theme.bg.secondary,
        color: theme.text.primary,
        transition: `border-color ${transition.fast}, box-shadow ${transition.fast}`,
        cursor: 'pointer',
    };
    const priorityBtnBase = {
        padding: '4px 10px',
        border: `1px solid ${theme.border.default}`,
        borderRadius: radius.sm,
        backgroundColor: theme.bg.secondary,
        fontSize: typography.fontSize.sm,
        color: theme.text.tertiary,
        cursor: 'pointer',
        transition: `background-color ${transition.fast}, color ${transition.fast}, border-color ${transition.fast}`,
    };
    const priorityBtnActive = {
        backgroundColor: theme.interaction.selected,
        color: theme.status.primary,
        borderColor: theme.status.primary,
    };
    const sortOrderBtn = {
        padding: '4px 8px',
        border: `1px solid ${theme.border.default}`,
        borderRadius: radius.sm,
        backgroundColor: theme.bg.secondary,
        fontSize: typography.fontSize.base,
        cursor: 'pointer',
        color: theme.text.tertiary,
        transition: `background-color ${transition.fast}, color ${transition.fast}`,
    };
    const createBtn = {
        display: 'flex',
        alignItems: 'center',
        marginLeft: 'auto',
        padding: '6px 14px',
        backgroundColor: theme.status.primary,
        color: theme.text.inverse,
        border: 'none',
        borderRadius: radius.md,
        fontSize: typography.fontSize.sm,
        fontWeight: typography.fontWeight.medium,
        cursor: 'pointer',
        transition: `background-color ${transition.fast}, transform ${transition.fast}, box-shadow ${transition.fast}`,
        boxShadow: theme.shadow.sm,
    };
    /* -- 筛选工具栏 -- */
    const statusSelect = React.createElement('select', {
        value: filterStatus,
        onChange: (e) => setFilterStatus(e.target.value),
        style: toolSelect,
    }, ...STATUS_OPTIONS.map(opt => React.createElement('option', { key: opt.id, value: opt.id }, opt.label)));
    const priorityGroup = React.createElement('div', { style: { display: 'flex', gap: '4px' } }, ...PRIORITY_OPTIONS.map(opt => React.createElement('button', {
        key: opt.id,
        onClick: () => setFilterPriority(opt.id),
        style: { ...priorityBtnBase, ...(filterPriority === opt.id ? priorityBtnActive : {}) },
    }, opt.label)));
    const sortSelect = React.createElement('select', {
        value: sortBy,
        onChange: (e) => setSortBy(e.target.value),
        style: toolSelect,
    }, ...SORT_OPTIONS.map(opt => React.createElement('option', { key: opt.id, value: opt.id }, opt.label)));
    const sortOrderButton = React.createElement('button', {
        onClick: () => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc'),
        style: sortOrderBtn,
    }, sortOrder === 'asc' ? '↑' : '↓');
    const createButton = React.createElement('button', { onClick: handleCreate, style: createBtn }, plusIconNode, React.createElement('span', { style: { marginLeft: '4px' } }, '新建待办'));
    const toolbar = React.createElement('div', {
        style: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '16px',
            flexWrap: 'wrap',
        },
    }, statusSelect, priorityGroup, sortSelect, sortOrderButton, createButton);
    /* -- 内容区 -- */
    let contentElement;
    if (loading) {
        contentElement = React.createElement('div', {
            style: {
                padding: '40px',
                textAlign: 'center',
                color: theme.text.tertiary,
                fontSize: typography.fontSize.base,
            },
        }, React.createElement('span', { style: { animation: 'todo-pulse 1.5s ease-in-out infinite', display: 'inline-block' } }, '加载中...'));
    }
    else if (error) {
        contentElement = React.createElement('div', {
            style: {
                padding: '16px',
                backgroundColor: theme.status.dangerBg,
                border: `1px solid ${theme.status.danger}40`,
                borderRadius: radius.md,
                color: theme.status.danger,
                fontSize: typography.fontSize.base,
            },
        }, error);
    }
    else if (sortedTodos.length === 0 && !editorOpen) {
        contentElement = React.createElement('div', {
            style: {
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '60px 20px',
                gap: '8px',
            },
        }, emptyIconNode, React.createElement('p', { style: { margin: 0, fontSize: typography.fontSize.lg, fontWeight: typography.fontWeight.medium, color: theme.text.secondary } }, '暂无待办事项'), React.createElement('p', { style: { margin: 0, fontSize: typography.fontSize.sm, color: theme.text.tertiary } }, '点击「新建待办」创建第一条'));
    }
    else {
        const listItems = sortedTodos.map(todo => React.createElement(TodoListItem, {
            key: todo._id, todo, onEdit: handleEdit, onDelete: setDeleteConfirm, onComplete: handleComplete,
        }));
        contentElement = React.createElement('div', {
            style: { display: 'flex', flexDirection: 'column', gap: '6px' },
        }, ...listItems);
    }
    /* -- 分页 -- */
    let paginationElement = null;
    if (!loading && pagination && pagination.totalPages > 1) {
        const pageBtn = {
            padding: '6px 12px',
            border: `1px solid ${theme.border.default}`,
            borderRadius: radius.md,
            backgroundColor: theme.bg.secondary,
            fontSize: typography.fontSize.sm,
            cursor: 'pointer',
            color: theme.text.primary,
            transition: `background-color ${transition.fast}, border-color ${transition.fast}`,
        };
        paginationElement = React.createElement('div', {
            style: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginTop: '24px' },
        }, React.createElement('button', { onClick: () => setPage(Math.max(1, pagination.page - 1)), disabled: pagination.page <= 1, style: pageBtn }, '上一页'), React.createElement('span', { style: { fontSize: typography.fontSize.sm, color: theme.text.tertiary } }, `${pagination.page} / ${pagination.totalPages}`), React.createElement('button', { onClick: () => setPage(Math.min(pagination.totalPages, pagination.page + 1)), disabled: pagination.page >= pagination.totalPages, style: pageBtn }, '下一页'));
    }
    /* -- 弹窗 -- */
    const editorModal = editorOpen
        ? React.createElement(TodoEditor, {
            todo: editingTodo ?? undefined,
            defaultGroupId: selectedGroupId,
            onSuccess: handleEditorSuccess,
            onCancel: handleEditorClose,
        })
        : null;
    const groupManagerModal = groupManagerOpen
        ? React.createElement(TodoGroupManager, {
            onClose: () => setGroupManagerOpen(false),
            onGroupsChange: refresh,
        })
        : null;
    let deleteModal = null;
    if (deleteConfirm) {
        const modalActions = { display: 'flex', justifyContent: 'flex-end', gap: '8px' };
        deleteModal = React.createElement('div', {
            style: {
                position: 'fixed', inset: 0, backgroundColor: theme.bg.overlay,
                display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
                padding: '20px',
            },
            onClick: () => setDeleteConfirm(null),
        }, React.createElement('div', {
            style: {
                backgroundColor: theme.bg.elevated, borderRadius: radius.xl, padding: '24px',
                width: '360px', maxWidth: '90vw', boxShadow: theme.shadow.xl,
                animation: 'todo-scale-in 0.2s ease forwards',
            },
            onClick: (e) => e.stopPropagation(),
        }, React.createElement('h3', { style: { margin: '0 0 12px 0', fontSize: typography.fontSize.xl, fontWeight: typography.fontWeight.semibold, color: theme.text.primary } }, '确认删除'), React.createElement('p', { style: { margin: '0 0 20px 0', fontSize: typography.fontSize.base, color: theme.text.secondary, lineHeight: typography.lineHeight.relaxed } }, `确定要删除待办「${deleteConfirm.title}」吗？删除后无法恢复。`), React.createElement('div', { style: modalActions }, React.createElement('button', { onClick: () => setDeleteConfirm(null), disabled: deleting, style: {
                padding: '8px 16px', border: `1px solid ${theme.border.default}`, borderRadius: radius.md,
                backgroundColor: theme.bg.secondary, fontSize: typography.fontSize.base, cursor: 'pointer', color: theme.text.secondary,
                transition: `background-color ${transition.fast}`,
            } }, '取消'), React.createElement('button', { onClick: handleDeleteConfirm, disabled: deleting, style: {
                padding: '8px 16px', border: 'none', borderRadius: radius.md,
                backgroundColor: theme.status.danger, fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.medium,
                color: '#fff', cursor: 'pointer',
                transition: `background-color ${transition.fast}, opacity ${transition.fast}`,
                opacity: deleting ? 0.6 : 1,
            } }, deleting ? '删除中...' : '确认删除'))));
    }
    /* -- 分组选择器（桌面端显示在左侧） -- */
    const groupSelector = !isMobile
        ? React.createElement('div', {
            style: {
                width: '200px', flexShrink: 0,
                borderRight: `1px solid ${theme.border.divider}`,
                paddingRight: '16px',
            },
        }, React.createElement(TodoGroupSelector, {
            selectedGroupId, onSelect: setSelectedGroupId, onManage: () => setGroupManagerOpen(true),
        }))
        : React.createElement('div', { style: { marginBottom: '12px' } }, React.createElement(TodoGroupSelector, {
            selectedGroupId, onSelect: setSelectedGroupId, compact: true,
        }));
    /* -- 主区域 -- */
    const main = React.createElement('div', {
        style: {
            flex: 1,
            paddingLeft: isMobile ? 0 : '20px',
            minWidth: 0,
        },
    }, toolbar, contentElement, paginationElement);
    return React.createElement('div', {
        style: {
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: 0,
            maxWidth: '960px',
            margin: '0 auto',
            padding: isMobile ? '16px' : '24px',
            fontFamily: typography.fontFamily,
            minHeight: '500px',
        },
    }, groupSelector, main, editorModal, groupManagerModal, deleteModal);
}
//# sourceMappingURL=todo-panel.js.map